import { Button, Icon, Table, TablePagination } from '@octano/global-ui';
import { CellFormatOptions } from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DisplayError from '../../../../components/info/DisplayError';
import DisplayInfo from '../../../../components/info/DisplayInfo';
import Loading from '../../../../components/info/Loading';
import { TextEllipsis } from '../../../../components/text';
import { PathsLayouts } from '../../../../config/routes';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { TableColumns } from '../../../../types/periodTuition';
import { Discount as DiscountType, getDiscountsRequest } from '../api/discount';
import { DeleteDiscountModal } from './parts/DeleteDiscountModal';

import styles from '../style.module.scss';

function Discount() {
  const [discounts, setDiscounts] = useState<DiscountType[]>([]);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const history = useHistory();
  const { t } = useTranslation();
  const prefix = 'tariffAndTuition.discounts';

  //Discounts columns
  const columns: TableColumns[] = [
    {
      columnName: 'code',
      headerText: t(`${prefix}.discount.code`),
      bold: true,
    },
    {
      columnName: 'name',
      headerText: t(`${prefix}.discount.name`),
    },
    {
      columnName: 'description',
      headerText: t(`${prefix}.discount.description`),
      cellFormat: (options: CellFormatOptions) => (
        <TextEllipsis key={options.index} textWidth={350}>
          {options.value}
        </TextEllipsis>
      ),
    },
    {
      columnName: 'percentage',
      headerText: t(`${prefix}.discount.percentage`),
      cellFormat: (options: CellFormatOptions) =>
        `${Math.round(Number(options.value))}%`,
    },
    {
      columnName: 'id',
      headerText: '',
      width: '40px',
      cellFormat: (options: CellFormatOptions) => (
        <div className="d-flex justify-content-end">
          <span
            onClick={() => {
              history.push(
                `${PathsLayouts.tariffAndTuiton}/discounts/edit/${options.row.id}`,
              );
            }}
            className="cursor-pointer"
          >
            <Icon name="edit" key="edit" color="primary" />
          </span>
          <span
            onClick={() => {
              setItemToDelete(options.row.id);
              setShowModalDelete(true);
            }}
            className="cursor-pointer ml-2"
          >
            <Icon name="trash" key="edit" color="secondary" />
          </span>
        </div>
      ),
    },
  ];

  /**
   * getDiscounts request
   * @param items quantity items
   * @param page page to load
   */

  const getDiscounts = useCallback(
    async (items: number = 10, page: number = 0) => {
      const { data, error } = await getDiscountsRequest(items, page);

      if (error) {
        setErrorLoading(error.code);
        return;
      }

      if (data) {
        setErrorLoading(undefined);
        const { data: discountItems = [], total = 0, total_pages = 1 } = data;
        setDiscounts(discountItems);
        setTotalItems(total);
        setTotalPages(total_pages);
        setShowPagination(true);
        setCurrentPage(page + 1);
      }

      setLoading(false);
    },
    [setErrorLoading, setLoading],
  );

  useEffect(() => {
    getDiscounts();
  }, [getDiscounts]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => setLoading(true)}
      />
    );
  }
  if (loading) {
    return <Loading insideCard className="mt-4" />;
  }

  // Show list view
  return (
    <div className="g-table-container">
      <Button
        text={t(`${prefix}.newDiscount`)}
        type="button"
        size="sm"
        icon="plus"
        onClick={() =>
          history.push(`${PathsLayouts.tariffAndTuiton}/discounts/create`)
        }
        className="g-add-button mb-3 mt-3"
      />

      <div className={styles.list}>
        <Table columns={columns} data={discounts} noResultsText={<div></div>} />
        {discounts.length === 0 ? (
          <DisplayInfo
            insideCard
            title={t(`${prefix}.discountTableEmptyTitle`)}
            textBody={t(`${prefix}.discountTableEmptySubtitle`)}
          />
        ) : null}
        {showPagination && totalItems > 10 && (
          <TablePagination
            pagination={{
              currentPage: currentPage,
              itemsPerPage: 10,
              onChangePage: (page) => {
                getDiscounts(10, page - 1);
              },
              totalItems: totalItems,
              totalPages: totalPages,
            }}
          />
        )}
      </div>

      <DeleteDiscountModal
        show={showModalDelete}
        toggleModal={() => {
          setShowModalDelete(!showModalDelete);
        }}
        reloadDiscountList={getDiscounts}
        itemToDelete={itemToDelete}
      />
    </div>
  );
}

export default Discount;
